import { resolveCameraLabelAnchor } from '../geometry/resolve-camera-label-anchor';
import { createSvgElement } from '../svg';
import type { MapRenderCameraModel } from '../types';

/**
 * Builds the non-interactive SVG group for every camera label in the visual model.
 *
 * @param cameras Immutable camera placements to convert into label elements.
 * @returns Detached group carrying the stable `data-map-layer="camera-labels"` hook.
 */
export function renderCameraLabelsLayer(cameras: readonly MapRenderCameraModel[]): SVGGElement {
  const layerElement = createSvgElement('g');
  layerElement.dataset.mapLayer = 'camera-labels';
  layerElement.setAttribute('pointer-events', 'none');

  for (const camera of cameras) {
    layerElement.append(createCameraLabelElement(camera));
  }

  return layerElement;
}

/**
 * Builds one semantic camera label element for full rendering or a partial camera update.
 *
 * @param camera Immutable camera placement providing label content and semantic identifiers.
 * @returns Detached text element with renderer-owned entity hooks; it is non-interactive by design.
 */
export function createCameraLabelElement(camera: MapRenderCameraModel): SVGTextElement {
  const anchorPoint = resolveCameraLabelAnchor(camera);
  const textElement = createSvgElement('text');
  textElement.textContent = camera.label.text;
  textElement.setAttribute('x', String(anchorPoint.x));
  textElement.setAttribute('y', String(anchorPoint.y));
  textElement.setAttribute('fill', camera.label.color);
  textElement.setAttribute('font-size', String(camera.label.fontSize));
  textElement.setAttribute('font-family', 'ui-monospace, SFMono-Regular, Menlo, monospace');
  textElement.setAttribute('font-weight', '700');
  textElement.setAttribute('text-anchor', 'middle');
  textElement.setAttribute('dominant-baseline', 'middle');
  textElement.dataset.mapEntityKind = 'camera-label';
  textElement.dataset.mapEntityId = String(camera.mapPositionId);
  textElement.dataset.mapCameraId = String(camera.cameraId);

  return textElement;
}
